import { FormattedEvent } from '@eventbrite/event-renderer';
import { EventInteractionContext } from '@eventbrite/event-tracker';

export function aggregateTrackingContext({
    id,
    eventSearch,
    searchId,
    events = [],
    userId,
    currentPage,
    locale,
    sessionId,
    guestId,
}: {
    id: string;
    eventSearch: any;
    searchId: string;
    events: FormattedEvent[] | undefined;
    userId: string;
    currentPage: number;
    locale?: string;
    sessionId: string;
    guestId: string;
}): EventInteractionContext {
    const SEARCH_PLACEMENT_NAME = 'search';

    const event: FormattedEvent | undefined = events.find(
        (event: FormattedEvent) => event.id === id,
    );
    const eventPosition = computeRanks(event, events);

    return {
        user: {
            id: userId,
            locale,
            guestId,
            sessionId,
        },
        adId: event?.promotedListingMetadata?.adId,
        place: {
            name: SEARCH_PLACEMENT_NAME,
            position: eventPosition.rank,
            absoluteRank: eventPosition.absoluteRank,
            page: currentPage,
            context: {
                date_filter: eventSearch.dates || null,
                query_terms: eventSearch.q || null,
                search_id: searchId,
                ...computeLocationInfo(eventSearch),
            },
            placementId: event?.promotedListingMetadata?.placementId,
        },
        venueId: event?.venue?.id,
    };
}

/* Due to historical reasons we need to compute both the relative position of an add and it's absolute
 * The relative position (rank) is assuming promoted and organic ads are different lists and reset their rank
 * For example, if we had this series of events: 1Promoted - 2Organic - 3Organic - 4Promoted
 * Their positions would be as follows:
 * 1Promoted - rank: 1, absoluteRank: 1
 * 2Organic - rank: 1, absoluteRank: 2
 * 3Organic - rank: 2, absoluteRank: 3
 * 4Promoted - rank 2, absoluteRank: 4
 * TODO: Unify this approach and have only one rank (absoluteRank)
 */
function computeRanks(
    eventToSearch: FormattedEvent | undefined,
    events: FormattedEvent[],
) {
    if (!eventToSearch) {
        return {
            rank: 0,
            absoluteRank: 0,
        };
    }

    const { isPromoted, id } = eventToSearch;
    const eventsGroup = events.filter((e) => isPromoted === e.isPromoted);

    return {
        rank: eventsGroup.findIndex((e) => e.id === id) + 1,
        absoluteRank: events.findIndex((e) => e.id === id) + 1,
    };
}

export function computeLocationInfo(eventSearch: any) {
    const placeId = Array.isArray(eventSearch.places)
        ? eventSearch.places[0]
        : null;

    if (eventSearch.onlineEventsOnly) {
        return {
            is_online: true,
        };
    }

    if (eventSearch.bbox) {
        const { latitude, longitude } = getBboxCenterCoordinates(
            eventSearch.bbox,
        );
        return {
            is_online: false,
            latitude,
            longitude,
            place_id: placeId,
        };
    }

    return {
        is_online: false,
        place_id: placeId,
    };
}

export function getBboxCenterCoordinates(bbox: string) {
    try {
        const [swLng, swLat, neLng, neLat] = bbox.split(',').map(Number);
        const latitude = `${(neLat + swLat) / 2}`;
        const longitude = `${(neLng + swLng) / 2}`;
        return {
            latitude,
            longitude,
        };
    } catch (e) {
        return { latitude: undefined, longitude: undefined };
    }
}
